<template>
  <div
    v-if="shouldRender"
    class="vf-o-breadcrumb-nav"
    :class="{
      'vf-o-breadcrumb-nav--product': isProductRoute,
      'vf-o-breadcrumb-nav--reserved': reserveSpace,
    }"
  >
    <nav v-if="breadcrumbs.length" aria-label="Breadcrumb">
      <ol class="breadcrumb">
        <li
          v-for="breadcrumb in breadcrumbs"
          :key="`${breadcrumb.path}:${breadcrumb.name}`"
          class="breadcrumb-item"
          :class="{ active: isActive(breadcrumb) }"
        >
          <router-link v-if="!isActive(breadcrumb)" :to="breadcrumb.path">
            {{ breadcrumb.name }}
          </router-link>
          <span v-else aria-current="page">{{ breadcrumb.name }}</span>
        </li>
      </ol>
    </nav>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import {
  buildFallbackRouteBreadcrumbs,
  normalizeBreadcrumbPath,
  toNavigationBreadcrumbs,
} from "../../../utils/breadcrumbTrail";
import { toCrawlableInternalPath } from "../../../utils/canonicalPath";

const props = defineProps({
  items: {
    type: Array,
    default: undefined,
  },
  // Async entity pages can reserve the compact breadcrumb row before their
  // canonical trail arrives. This keeps the catalog controls and card
  // skeletons at the same document position on slow connections.
  reserveSpace: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const store = useStore();

const managedBreadcrumbs = computed(
  () => store.getters["common/breadcrumbs/get"] || []
);

const resolvedSeoRoute = computed(
  () => store.state?.vuefront?.resolvedSeoRoute || {}
);

const isProductRoute = computed(() => {
  if (route.path.includes("/store/product/")) return true;

  const isSeoCatchAll = route.matched?.[0]?.name === "_slug";
  const resolved = resolvedSeoRoute.value;

  return (
    isSeoCatchAll &&
    resolved?.type === "product" &&
    resolved?.path === route.path
  );
});

const explicitBreadcrumbs = computed(() => {
  if (!Array.isArray(props.items)) return null;

  return props.items
    .map((item: any) => ({
      name: String(item?.name || "").trim(),
      path: toCrawlableInternalPath(item?.path),
    }))
    .filter((item: any) => item.name && item.path);
});

const ENTITY_ROUTE_TYPES = new Set([
  "category",
  "page",
  "manufacturer",
  "brand",
  "manufacturer_info",
  "manufacturer-info",
  "manufacturerInfo",
  "product",
]);

const routeOwnsBreadcrumb = computed(() => {
  const directEntityRoute =
    /^\/(?:store\/(?:category|manufacturer|product)|page)\//.test(route.path);
  if (directEntityRoute) return true;

  const resolved = resolvedSeoRoute.value;
  return (
    route.matched?.[0]?.name === "_slug" &&
    resolved?.path === route.path &&
    ENTITY_ROUTE_TYPES.has(String(resolved?.type || ""))
  );
});

const explicitMatchesRoute = computed(() => {
  const items = explicitBreadcrumbs.value;
  if (!items?.length) return false;

  return (
    normalizeBreadcrumbPath(items[items.length - 1]?.path) ===
    normalizeBreadcrumbPath(route.path)
  );
});

// Entity pages populate their canonical trail inside async page setup. During
// SSR the layout renders before its page slot, so its global instance would
// otherwise publish a stale Home-only/slug fallback. The page-local instance
// passes explicit items after setup has completed and remains visible.
const shouldRender = computed(() =>
  explicitBreadcrumbs.value
    ? props.reserveSpace || isProductRoute.value || explicitMatchesRoute.value
    : !routeOwnsBreadcrumb.value
);

const breadcrumbs = computed(() => {
  if (explicitBreadcrumbs.value) return explicitBreadcrumbs.value;

  const managed = toNavigationBreadcrumbs(managedBreadcrumbs.value);
  const managedCurrent = managed[managed.length - 1];
  const managedMatchesRoute =
    managed.length > 1 &&
    normalizeBreadcrumbPath(managedCurrent?.path) ===
      normalizeBreadcrumbPath(route.path);

  // Category, manufacturer and product pages publish a human, canonical trail
  // through Vuex during SSR. Prefer it whenever its current item belongs to the
  // current route; the guard prevents a stale trail flashing during SPA nav.
  if (managedMatchesRoute || (isProductRoute.value && managed.length > 1)) {
    return managed;
  }

  // A product slug is not a usable breadcrumb label. Until its canonical
  // managed trail resolves, expose Home alone rather than the raw SEO slug.
  if (isProductRoute.value) {
    return toNavigationBreadcrumbs(managedBreadcrumbs.value);
  }

  return buildFallbackRouteBreadcrumbs(route.path);
});

const isActive = (breadcrumb: any) => {
  return (
    normalizeBreadcrumbPath(breadcrumb?.path) ===
    normalizeBreadcrumbPath(route.path)
  );
};
</script>
